import { useTranslation } from 'react-i18next';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IColumnType, Table } from '../../../../../components/Table/Table';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { getVerticalDropsType } from '../../../../utils/utils';
import { IVerticalDropsResults } from '../../../api/IApiResponse';
import { EVerticalDropsTypes } from '../../../enum/general';
import { IProjectMacra2 } from '../../../store/project/projectSlice';
import { IChannelGeometryVerticalDropForm } from '../../InputForm/ChannelGeometry/IChannelGeometry';
import { IWingsGeometryVerticalDropForm } from '../../InputForm/WingsGeometry/IWingsGeometry';
import { IStillingBasinVerticalDropForm } from '../../InputForm/StillingBasin/IStillingBasin';

interface IHydraulicVerticalProps {}

export interface IData {
    label: string;
    symbol: string | JSX.Element;
    value: number | string | boolean;
    unit: string;
    check?: boolean;
    valueFullRow?: boolean;
}

const HydraulicVertical = (props: IHydraulicVerticalProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<IVerticalDropsResults> = useAppSelector(selectResults as any);
    const project: IInitialState<IProjectMacra2> = useAppSelector(selectProject as any);
    const projectInfo = useAppSelector(selectProjectInfo);

    const type: EVerticalDropsTypes = getVerticalDropsType(project.data?.stillingBasin!);

    const mainInput: IData[] = [
        {
            label: t('output_hydraulic_vertical_design_discharge'),
            symbol: 'Q',
            value: roundToTwo(project.data?.hydraulicData?.discharge),
            unit: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_roughness_coefficient'),
            symbol: 'n',
            value: roundToTwo(project.data?.hydraulicData?.roughnessCoefficient),
            unit: '-',
        },
        {
            label: t('output_hydraulic_vertical_river_gradient'),
            symbol: 'i',
            value: roundToTwo(project.data?.hydraulicData?.riverBedGradient),
            unit: conversionLabel.mm_ftft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_channel_width'),
            symbol: 'b',
            value: roundToTwo(project.data?.geometry?.channelWidth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_channel_height'),
            symbol: 'H',
            value: roundToTwo(project.data?.wingsGeometry?.height),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_left_bank_slope'),
            symbol: <SmallLetter normal='θ' small='1' />,
            value: roundToTwo((project.data?.geometry as IChannelGeometryVerticalDropForm).slopeOfLeftBank),
            unit: 'deg',
        },

        {
            label: t('output_hydraulic_vertical_right_bank_slope'),
            symbol: <SmallLetter normal='θ' small='r' />,
            value: roundToTwo((project.data?.geometry as IChannelGeometryVerticalDropForm).slopeOfRightBank),
            unit: 'deg',
        },
        {
            label: t('output_hydraulic_vertical_weir_crest_elevation'),
            symbol: <SmallLetter normal='f' small='g' />,
            value: roundToTwo(results.data?.hydraulicResult.weirCrestElevation),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_weir_crest_width'),
            symbol: <SmallLetter normal='L' small='g' />,
            value: roundToTwo((project.data?.wingsGeometry as IWingsGeometryVerticalDropForm).width),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_vertical_crest_side_slope_angle'),
            symbol: <SmallLetter normal='θ' small='g' />,
            value: roundToTwo((project.data?.wingsGeometry as IWingsGeometryVerticalDropForm).slopeAngle),
            unit: 'deg',
        },
        {
            label: t('output_hydraulic_vertical_granular_material_downstream'),
            symbol: <SmallLetter normal='d' small='90' />,
            value: roundToTwo(project.data?.soilData?.granularMaterialDownstream),
            unit: conversionLabel.mm_in[projectInfo.data?.unitOfMeasure as number],
        },
    ];
    let _label: string = '';
    let _input: IData[] = [];
    let _hydraulicResults: IData[] = [];
    switch (type) {
        case EVerticalDropsTypes.UnlinedVerticalDrop:
            _label = 'CASE 1 - UNLINED VERTICAL DROP';
            _input = mainInput;
            _hydraulicResults = [
                {
                    label: t('output_hydraulic_vertical_water_depth_crest'),
                    symbol: <SmallLetter normal='d' small='g' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOnCrest),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    symbol: <SmallLetter normal='d' small='3' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthDownstreamTheStructure),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_backwater_depth'),
                    symbol: <SmallLetter normal='d' small='0' />,
                    value: roundToTwo(results.data?.hydraulicResult.backwaterDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_discharge_coefficient'),
                    symbol: <SmallLetter normal='C' small='d' />,
                    value: roundToTwo(results.data?.hydraulicResult.dischargeCoefficient),
                    unit: '-',
                },
                {
                    label: t('output_hydraulic_vertical_main_weir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_water_cushion_elevation'),
                    symbol: <SmallLetter normal='z' small='v' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterCushionElevationAtWeirToe),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    symbol: <SmallLetter normal='d' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOfSupercriticalFlow),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_average_velocity_channel'),
                    symbol: <SmallLetter normal='V' small='m' />,
                    value: roundToTwo(results.data?.hydraulicResult.averageVelocityInChannel),
                    unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            break;
        case EVerticalDropsTypes.UnlinedPoolAndCountweir:
            _label = 'CASE 2 - UNLINED POOL + COUNTERWEIR';
            _input = [
                ...mainInput,
                {
                    label: t('output_hydraulic_vertical_counterweir_elevation'),
                    symbol: <SmallLetter normal='f' small='c' />,
                    value: roundToTwo(project.data?.stillingBasin?.counterweirElevation),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_counterweir_width'),
                    symbol: <SmallLetter normal='L' small='c' />,
                    value: roundToTwo(project.data?.stillingBasin?.width),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_basin_length_designed'),
                    symbol: <SmallLetter normal='L' small='b' />,
                    value: roundToTwo(project.data?.stillingBasin?.poolLength),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            _hydraulicResults = [
                {
                    label: t('output_hydraulic_vertical_water_depth_crest'),
                    symbol: <SmallLetter normal='d' small='g' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOnCrest),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    symbol: <SmallLetter normal='d' small='3' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthDownstreamTheStructure),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_backwater_depth'),
                    symbol: <SmallLetter normal='d' small='0' />,
                    value: roundToTwo(results.data?.hydraulicResult.backwaterDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_main_weir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_water_cushion_elevation'),
                    symbol: <SmallLetter normal='z' small='v' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterCushionElevationAtWeirToe),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    symbol: <SmallLetter normal='d' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOfSupercriticalFlow),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_froude_number_supercritical_flow'),
                    symbol: <SmallLetter normal='Fr' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.froudeNumberOfSupercriticalFlow),
                    unit: '-',
                },
                {
                    label: t('output_hydraulic_vertical_counterweir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    symbol: <SmallLetter normal='d' small='2' />,
                    value: roundToTwo(results.data?.hydraulicResult.tailwaterDepthDueToCounterweir),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
            _label = 'CASE 3 - LINED POOL + COUNTERWEIR (fb=0)';
            _input = [
                ...mainInput,
                {
                    label: t('output_hydraulic_vertical_counterweir_elevation'),
                    symbol: <SmallLetter normal='f' small='c' />,
                    value: roundToTwo(project.data?.stillingBasin?.counterweirElevation),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_counterweir_width'),
                    symbol: <SmallLetter normal='L' small='c' />,
                    value: roundToTwo(project.data?.stillingBasin?.width),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_basin_length_designed'),
                    symbol: <SmallLetter normal='L' small='b' />,
                    value: roundToTwo(project.data?.stillingBasin?.poolLength),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            _hydraulicResults = [
                {
                    label: t('output_hydraulic_vertical_water_depth_crest'),
                    symbol: <SmallLetter normal='d' small='g' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOnCrest),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    symbol: <SmallLetter normal='d' small='3' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthDownstreamTheStructure),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_backwater_depth'),
                    symbol: <SmallLetter normal='d' small='0' />,
                    value: roundToTwo(results.data?.hydraulicResult.backwaterDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_main_weir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_water_cushion_elevation'),
                    symbol: <SmallLetter normal='z' small='v' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterCushionElevationAtWeirToe),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    symbol: <SmallLetter normal='d' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOfSupercriticalFlow),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_froude_number_supercritical_flow'),
                    symbol: <SmallLetter normal='Fr' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.froudeNumberOfSupercriticalFlow),
                    unit: '-',
                },
                {
                    label: t('output_hydraulic_vertical_counterweir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_min_counterweir_elevation'),
                    symbol: <SmallLetter normal='f' small='c_min' />,
                    value: roundToTwo(results.data?.hydraulicResult.minimumCounterweirElevation),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_verified_counterweir_elevation'),
                    symbol: '',
                    value: results.data?.hydraulicResult?.minimumBasinElevation! < (project.data as IProjectMacra2)?.stillingBasin?.counterweirElevation!,
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    symbol: <SmallLetter normal='d' small='2' />,
                    value: roundToTwo(results.data?.hydraulicResult.tailwaterDepthDueToCounterweir),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
            _label = 'CASE 4 - LINED POOL + COUNTERWEIR (fb<0)';
            _input = [
                ...mainInput,
                {
                    label: t('output_hydraulic_vertical_counterweir_width'),
                    symbol: <SmallLetter normal='L' small='c' />,
                    value: roundToTwo(project.data?.stillingBasin?.width),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_basin_elevation_designed'),
                    symbol: 'fb',
                    value: roundToTwo(project.data?.stillingBasin?.stillingBasinElevation),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_basin_length_designed'),
                    symbol: <SmallLetter normal='L' small='b' />,
                    value: roundToTwo(project.data?.stillingBasin?.poolLength),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
            ];
            _hydraulicResults = [
                {
                    label: t('output_hydraulic_vertical_water_depth_crest'),
                    symbol: <SmallLetter normal='d' small='g' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOnCrest),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    symbol: <SmallLetter normal='d' small='3' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthDownstreamTheStructure),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_backwater_depth'),
                    symbol: <SmallLetter normal='d' small='0' />,
                    value: roundToTwo(results.data?.hydraulicResult.backwaterDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_main_weir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_water_cushion_elevation'),
                    symbol: <SmallLetter normal='z' small='v' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterCushionElevationAtWeirToe),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    symbol: <SmallLetter normal='d' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.waterDepthOfSupercriticalFlow),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_froude_number_supercritical_flow'),
                    symbol: <SmallLetter normal='Fr' small='1' />,
                    value: roundToTwo(results.data?.hydraulicResult.froudeNumberOfSupercriticalFlow),
                    unit: '-',
                },
                {
                    label: t('output_hydraulic_vertical_counterweir_submerged'),
                    symbol: '',
                    value: results.data?.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(),
                    unit: '',
                },
                {
                    label: t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    symbol: <SmallLetter normal='d' small='2' />,
                    value: roundToTwo(results.data?.hydraulicResult.tailwaterDepthDueToCounterweir),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_min_basin_elevation'),
                    symbol: <SmallLetter normal='f' small='b_min' />,
                    value: roundToTwo(results.data?.hydraulicResult.minimumBasinElevation),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                },
                {
                    label: t('output_hydraulic_vertical_verified_basin_elevation'),
                    symbol: '',
                    value:
                        type === EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth &&
                        results.data?.hydraulicResult?.minimumBasinElevation! < Math.abs(project.data?.stillingBasin?.stillingBasinElevation!),

                    unit: '',
                },
            ];
            break;

        default:
            break;
    }

    const columnsHydraulicResults: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('graphical_results_hydraulic'),
        },
        {
            key: 'symbol',
            title: t('output_symbol'),
        },
        {
            key: 'value',
            title: t('output_value'),
            render: (_, row) => {
                if (typeof row.value === 'boolean') {
                    if (row.value) return <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />;
                    else return <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />;
                } else {
                    return row.value;
                }
            },
        },
        {
            key: 'unit',
            title: t('output_unit'),
        },
    ];
    const columnsInput: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_table_title_input'),
        },
        {
            key: 'symbol',
            title: t('output_symbol'),
        },
        {
            key: 'value',
            title: t('output_value'),
            render: (_, row) => {
                if (typeof row.value === 'boolean') {
                    if (row.value) return <i className='fa-solid fa-check  d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />;
                    else return <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />;
                } else {
                    return row.value;
                }
            },
        },
        {
            key: 'unit',
            title: t('output_unit'),
        },
    ];

    return (
        <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85'>
            <label>{_label}</label>
            <div className='d-flex justify-content-evenly align-items-center gap-3 w-100 static-results'>
                <Table columns={columnsInput} data={_input} className='w-100' />
                <Table columns={columnsHydraulicResults} data={_hydraulicResults} className='w-100' />
            </div>
        </div>
    );
};

export default HydraulicVertical;
